import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Plus, Trash2, ArrowUp, ArrowDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = () => ({ _key: Math.random().toString(36).slice(2), title: "", description: "",
  requirement_level: "mandatory", applicability_mode: "always", conditional_guidance: "",
  completion_authority_mode: "subject_editor", completion_permission_key: "" });

// Draft-only builder. A published version is immutable; editing it here creates a
// new draft (backend). Publishing activates the new immutable version.
export function ChecklistTemplateEditor({ templateId, canPublish = true, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ name: "", description: "" });
  const [items, setItems] = useState([]);
  const [permOptions, setPermOptions] = useState([]);

  useEffect(() => {
    apiFetch(`/checklists/templates/${templateId}/draft`)
      .then((d) => { setMeta({ name: d.template.name, description: d.template.description || "" });
        setItems((d.items || []).map((i) => ({ ...i, _key: i.id }))); })
      .catch((e) => toast.error("Could not open editor", { description: e.message }))
      .finally(() => setLoading(false));
    apiFetch("/permissions").then((p) => setPermOptions(Array.isArray(p) ? p : [])).catch(() => {});
  }, [templateId]);
  useEffect(() => { const k = (e) => {
    if (e.key !== "Escape") return;
    // don't close the drawer if a nested Radix Select/popover or dialog is open
    if (document.querySelector('[data-radix-popper-content-wrapper]') || document.querySelector('[role="dialog"] [role="listbox"]')) return;
    onClose();
  }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);

  const setItem = (key, patch) => setItems((p) => p.map((i) => (i._key === key ? { ...i, ...patch } : i)));
  const move = (idx, dir) => setItems((p) => { const n = [...p]; const j = idx + dir; if (j < 0 || j >= n.length) return p; [n[idx], n[j]] = [n[j], n[idx]]; return n; });

  const saveDraft = async () => {
    if (!meta.name.trim()) return toast.error("Please give the checklist a name.");
    if (items.some((i) => !i.title.trim())) return toast.error("Every item needs a title.");
    setSaving(true);
    try {
      const body = { name: meta.name.trim(), description: meta.description.trim(),
        items: items.map((i) => ({ title: i.title.trim(), description: i.description || "",
          requirement_level: i.requirement_level, applicability_mode: i.applicability_mode,
          conditional_guidance: i.applicability_mode === "conditional" ? (i.conditional_guidance || "") : "",
          completion_authority_mode: i.completion_authority_mode || "subject_editor",
          completion_permission_key: i.completion_authority_mode === "permission" ? (i.completion_permission_key || "") : "" })) };
      await apiFetch(`/checklists/templates/${templateId}/draft`, { method: "PUT", body: JSON.stringify(body) });
      toast.success("Draft saved");
      onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const publish = async () => {
    if (items.length === 0) return toast.error("Add at least one item before publishing.");
    await saveDraft();
    try {
      await apiFetch(`/checklists/templates/${templateId}/publish`, { method: "POST", body: "{}" });
      toast.success("Checklist published");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.message); }
  };

  return createPortal(
    <div className="fixed inset-0 z-drawer" data-testid="checklist-editor">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-border bg-card shadow-lift sm:w-[640px] lg:w-[720px] max-sm:inset-0">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-border px-5">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-accent" /> Edit Checklist</h2>
          <button onClick={onClose} data-testid="checklist-editor-close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
        </header>

        {loading ? <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div> : (
          <div className="flex-1 space-y-5 overflow-y-auto scrollbar-thin p-6">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} data-testid="checklist-name" />
            </div>
            <div>
              <Label className="text-xs">Description <span className="text-muted-foreground/60">(optional)</span></Label>
              <Input value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} data-testid="checklist-description" />
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground">
              Applies to <span className="font-medium text-foreground/80">Topics</span> · Gate <span className="font-medium text-foreground/80">Submit for Review</span>
            </div>

            <div className="space-y-3" data-testid="checklist-items">
              {items.map((it, idx) => (
                <div key={it._key} className="rounded-xl border border-border/70 p-3" data-testid={`checklist-item-${idx}`}>
                  <div className="flex items-start gap-2">
                    <Input value={it.title} onChange={(e) => setItem(it._key, { title: e.target.value })} placeholder="Criterion title…" className="flex-1" data-testid={`checklist-item-title-${idx}`} />
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button onClick={() => move(idx, -1)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title="Move up" data-testid={`checklist-item-up-${idx}`}><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => move(idx, 1)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title="Move down" data-testid={`checklist-item-down-${idx}`}><ArrowDown className="h-4 w-4" /></button>
                      <button onClick={() => setItems((p) => p.filter((x) => x._key !== it._key))} className="rounded p-1.5 text-destructive hover:bg-destructive/10" title="Remove" data-testid={`checklist-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <Textarea value={it.description} onChange={(e) => setItem(it._key, { description: e.target.value })} rows={2} placeholder="Guidance (optional)…" className="mt-2 text-sm" />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Select value={it.requirement_level} onValueChange={(v) => setItem(it._key, { requirement_level: v })}>
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`checklist-item-req-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="mandatory">Mandatory</SelectItem><SelectItem value="recommended">Recommended</SelectItem></SelectContent>
                    </Select>
                    <Select value={it.applicability_mode} onValueChange={(v) => setItem(it._key, { applicability_mode: v })}>
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`checklist-item-appl-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="always">Always applies</SelectItem><SelectItem value="conditional">Conditional</SelectItem></SelectContent>
                    </Select>
                    <span className="text-[11px] text-muted-foreground">Manual</span>
                  </div>
                  {it.applicability_mode === "conditional" && (
                    <Input value={it.conditional_guidance} onChange={(e) => setItem(it._key, { conditional_guidance: e.target.value })}
                      placeholder="When does this apply? (shown to reviewers)" className="mt-2 text-sm" data-testid={`checklist-item-cond-${idx}`} />
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-muted-foreground">Who can complete this?</span>
                    <Select value={it.completion_authority_mode || "subject_editor"} onValueChange={(v) => setItem(it._key, { completion_authority_mode: v })}>
                      <SelectTrigger className="h-8 w-[190px]" data-testid={`checklist-item-authority-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subject_editor">Subject editors</SelectItem>
                        <SelectItem value="permission">Users with specific permission</SelectItem>
                      </SelectContent>
                    </Select>
                    {it.completion_authority_mode === "permission" && (
                      <Select value={it.completion_permission_key || ""} onValueChange={(v) => setItem(it._key, { completion_permission_key: v })}>
                        <SelectTrigger className="h-8 w-[220px]" data-testid={`checklist-item-permkey-${idx}`}><SelectValue placeholder="Choose permission…" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                          {permOptions.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setItems((p) => [...p, blank()])} data-testid="checklist-add-item"><Plus className="h-4 w-4" /> Add Item</Button>
            </div>
          </div>
        )}

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border p-4">
          <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="checklist-save-draft">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Draft"}</Button>
          {canPublish
            ? <Button onClick={publish} disabled={saving} data-testid="checklist-publish">Publish</Button>
            : <span className="text-[12px] text-muted-foreground" data-testid="checklist-publish-denied">Requires publishing permission.</span>}
        </footer>
      </aside>
    </div>,
    document.body
  );
}
